/**
 * @pageName BookingCalendar
 * @slotType dashboard plugin
 */
export interface ExtensionProps {
  notifyStartLoading?: () => void;
  notifyFinishedLoading?: () => void;

  liveCalendar3: {
    listenToLiveUpdates(params: {
      callback: (liveUpdateItems: LiveCalendar3UpdateItem[]) => void;
    }): {
      remove(): void;
    };
  };
}

/** @internal */
export type LiveCalendar3UpdateItem = {
  changeType: 'event-created' | 'event-updated';

  // Event ID
  id?: string | null;

  // Event title
  title?: string | null;

  // Information about when the event starts adjusted to the `timeZone` of the
  // business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
  adjustedStart?: ZonedDate;

  // Information about when the event ends adjusted to the `timeZone` of the
  // business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
  adjustedEnd?: ZonedDate;

  // Information about when the event starts.
  start?: ZonedDate;

  // Information about when the event ends.
  end?: ZonedDate;

  // Event status.
  status?: Status;

  // Information about whether the event is a single occurrence or part of a recurring series.
  recurrenceType?: RecurrenceType;
};
// noinspection JSUnusedGlobalSymbols

// NOTE: This file is for extension slot documentation, and should not include any imports

/** @internal */
export interface ZonedDate {
  /**
   * Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   */
  localDate?: string | null;

  /**
   * Time zone in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * For example, `America/New_York`.
   */
  timeZone?: string | null;

  /**
   * UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Not available for adjusted date fields.
   */
  utcDate?: Date | null;
}

/** @internal */
export declare enum Status {
  // Event is scheduled to happen or has happened.
  CONFIRMED = 'CONFIRMED',

  //  Event has been canceled.
  CANCELLED = 'CANCELLED',
}

/** @internal */
export declare enum RecurrenceType {
  // The event occurs only once and doesn't repeat.
  NONE = 'NONE',

  // Defines the recurrence pattern for a series of recurring events.
  MASTER = 'MASTER',

  // A specific occurrence of a recurring event. You can't create an event with `{"recurrenceType": "INSTANCE"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.
  INSTANCE = 'INSTANCE',

  // A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.
  EXCEPTION = 'EXCEPTION',
}
/**
 * @pageName BookingCalendar
 * @slotType dashboard menu plugin
 */
export type CalendarMoreActionsMenuParams = {};
